import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Table from '../../components/Table/Table';
import TableTabs from '../../components/TableTabs/TableTabs';
import Empty from '../../components/Empty/Empty';
import Loader from '../../components/Loader/Loader';
import dots from '../../assets/dots.svg';
import { iconBucket } from '../../utils/icons';
import formatDate from '../../utils/formatDate';
import fixDateTimezone from '../../utils/fixDateTimezone';
import getVinBlacklist from '../../api/vinBlacklist/getVinBlacklist';
import deleteVinFromBlacklist from '../../api/vinBlacklist/deleteVinFromBlacklist';
import AddVinModal from './AddVinModal';
import useServerTable from './useServerTable';

const tableStyle = { backgroundColor: 'white' };

// Машины из базы с этим VIN: на них могут быть сделки, поэтому только показываем.
const CarsCell = ({ cars }) =>
  cars.length === 0
    ? '—'
    : cars.map((car) => (
        <div key={car.carId}>
          <Link to={`/cars/car/${car.carId}/main-data`}>
            {[car.mark, car.model].filter(Boolean).join(' ') || `ID ${car.carId}`}
          </Link>
          {car.stock > 0 ? `, в наличии: ${car.stock}` : ''}
        </div>
      ));

const VinBlacklist = () => {
  const [reload, setReload] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  // Ответ бэка о синхронизации: в контуре-копии список только для чтения, он ведётся в главном контуре
  const [sync, setSync] = useState(null);
  const replica = !!sync?.replica;

  const columns = useMemo(
    () => [
      { Header: 'VIN', accessor: 'vin', width: '25%' },
      { Header: 'Причина', accessor: (row) => row.reason || '—', width: '25%' },
      {
        Header: 'Добавлен',
        accessor: (row) => (row.createdAt ? formatDate(fixDateTimezone(row.createdAt), true) : ''),
        width: '20%',
      },
      {
        Header: 'В базе',
        accessor: 'cars',
        Cell: ({ value: cars }) => <CarsCell cars={cars || []} />,
        disableSortBy: true,
      },
      { Header: <img src={dots} alt="Dots" />, accessor: 'dots' },
    ],
    []
  );

  const { table, tableProps, loaded, loading, searching, value, setValue, search, setPagination } = useServerTable({
    fetcher: getVinBlacklist,
    key: 'vins',
    columns,
    reload,
    onData: (data) => setSync(data?.sync || null),
  });

  const popover = useMemo(
    () => [
      {
        title: 'Удалить',
        icon: iconBucket(),
        onClick: (row) =>
          deleteVinFromBlacklist(row?.id)
            .then(() => setReload((n) => n + 1))
            .catch(() => {}),
      },
    ],
    []
  );

  return (
    <>
      <TableTabs
        setFilter={search}
        value={value}
        setValue={setValue}
        navigate={() => setAddOpen(true)}
        button="Добавить"
        inputPlaceholder="Быстрый поиск по VIN"
        advancedSearch={false}
        // Копия чужого списка: кнопки «Добавить» нет
        addButton={!replica}
      />
      {replica && (
        <p style={{ margin: '10px 0 0', fontSize: 14, color: '#8b8b8b' }}>
          Список ведётся в основном контуре.{' '}
          {sync.lastSyncAt ? `Обновлён: ${formatDate(fixDateTimezone(sync.lastSyncAt), true)}.` : 'Ещё не обновлялся.'}
          {sync.error ? ` Не удалось обновить: ${sync.error}.` : ''}
        </p>
      )}
      <br />
      {!loaded ? (
        <Loader />
      ) : !table.page.length && !loading ? (
        <Empty title={searching ? 'По этому запросу ничего не найдено.' : 'В чёрном списке пока нет VIN.'} />
      ) : (
        <Table
          {...tableProps}
          all={'VIN'}
          style={tableStyle}
          api={'/api/crm/vin-blacklist'}
          response={'vins'}
          popover={replica ? undefined : popover}
          edit={!replica}
        />
      )}
      <AddVinModal
        isOpen={addOpen}
        onClose={() => setAddOpen(false)}
        onDone={() => {
          setAddOpen(false);
          setPagination((prev) => ({ ...prev, page: 1 }));
          setReload((n) => n + 1);
        }}
      />
    </>
  );
};

export default VinBlacklist;
